import { useState, useEffect } from "react";
import { Phone, Mail, Menu, X } from "lucide-react";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    setOpen(false);
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-black/70 border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between px-6 lg:px-10 h-16 lg:h-20">
        {/* Logo */}
        <button
          onClick={() => scrollTo("hero")}
          data-testid="navbar-logo"
          className="flex items-center gap-3 group"
        >
          <div className="w-11 h-11 lg:w-12 lg:h-12 flex items-center justify-center">
            <img
              src="https://customer-assets.emergentagent.com/job_strength-club-3/artifacts/47ayb25t_Screenshot%202026-04-14%20125312.png"
              alt="JerseyFit Barbell logo"
              className="w-full h-full object-contain"
              style={{ filter: "invert(1)", mixBlendMode: "screen" }}
            />
          </div>
          <span className="font-heading font-extrabold tracking-tight text-lg uppercase hidden sm:inline">
            JerseyFit <span className="text-[#0055FF]">Barbell</span>
          </span>
        </button>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-8">
          {[
            { id: "features", label: "The Gym" },
            { id: "training", label: "Training" },
            { id: "gallery", label: "Photos" },
            { id: "pricing", label: "Membership" },
            { id: "contact", label: "Contact" },
          ].map((item) => (
            <button
              key={item.id}
              onClick={() => scrollTo(item.id)}
              data-testid={`navbar-link-${item.id}`}
              className="text-sm text-white/70 hover:text-white transition-colors font-medium uppercase tracking-wider"
            >
              {item.label}
            </button>
          ))}
        </nav>

        {/* Desktop CTA */}
        <div className="hidden md:flex items-center gap-3">
          <a
            href="mailto:jfbarbell@gmail.com"
            data-testid="navbar-email-btn"
            className="btn-secondary px-4 py-2 text-sm font-bold uppercase tracking-wider inline-flex items-center gap-2"
          >
            <Mail size={15} />
            Email
          </a>
        </div>

        {/* Mobile toggle */}
        <button
          className="md:hidden text-white p-2"
          onClick={() => setOpen((v) => !v)}
          data-testid="navbar-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div
          data-testid="navbar-mobile-menu"
          className="md:hidden border-t border-white/10 bg-black/90 backdrop-blur-xl"
        >
          <div className="px-6 py-6 flex flex-col gap-4">
            {[
              { id: "features", label: "The Gym" },
              { id: "training", label: "Training" },
              { id: "gallery", label: "Photos" },
              { id: "pricing", label: "Membership" },
              { id: "contact", label: "Contact" },
            ].map((item) => (
              <button
                key={item.id}
                onClick={() => scrollTo(item.id)}
                data-testid={`navbar-mobile-link-${item.id}`}
                className="text-left text-white/80 hover:text-white text-base font-semibold uppercase tracking-wider"
              >
                {item.label}
              </button>
            ))}
            <div className="flex gap-3 pt-3">
              <a
                href="tel:6185500625"
                data-testid="navbar-mobile-call-btn"
                className="btn-primary px-4 py-3 text-sm font-bold uppercase tracking-wider inline-flex items-center gap-2 flex-1 justify-center"
              >
                <Phone size={15} />
                Call
              </a>
              <a
                href="mailto:jfbarbell@gmail.com"
                data-testid="navbar-mobile-email-btn"
                className="btn-secondary px-4 py-3 text-sm font-bold uppercase tracking-wider inline-flex items-center gap-2 flex-1 justify-center"
              >
                <Mail size={15} />
                Email
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
